import { useState, useCallback, useEffect } from 'react';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Card,
  CardHeader,
  Button,
  Select,
  Badge,
  Pagination,
  LoadingSkeleton,
  Input,
  Modal,
} from '@nexora/design-system';
import {
  useRfqs,
  useRfq,
  useCreateRfq,
  useSendRfq,
  useSubmitRfqResponse,
  useAwardRfq,
  useCancelRfq,
  type Rfq,
  type RfqItem,
  type CreateRfqInput,
  type SubmitRfqResponseInput,
} from './useProcurement';

/* ── Constants ── */

const RFQ_STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos' },
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'SENT', label: 'Enviado' },
  { value: 'QUOTED', label: 'Cotado' },
  { value: 'AWARDED', label: 'Adjudicado' },
  { value: 'EXPIRED', label: 'Expirado' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

function statusBadge(status: Rfq['status']) {
  const map: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
    DRAFT: { variant: 'neutral', label: 'Rascunho' },
    SENT: { variant: 'info', label: 'Enviado' },
    QUOTED: { variant: 'warning', label: 'Cotado' },
    AWARDED: { variant: 'success', label: 'Adjudicado' },
    EXPIRED: { variant: 'error', label: 'Expirado' },
    CANCELLED: { variant: 'error', label: 'Cancelado' },
  };
  return map[status] ?? { variant: 'info', label: status };
}

/* ── Page ── */

export function RfqListPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useRfqs({ status: statusFilter, page, limit: 20 });
  const sendMutation = useSendRfq();
  const cancelMutation = useCancelRfq();

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleSend = useCallback(async (id: string) => {
    try {
      await sendMutation.mutateAsync(id);
      showToast('RFQ enviado aos fornecedores.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao enviar RFQ.', 'error');
    }
  }, [sendMutation, showToast]);

  const handleCancelRfq = useCallback(async (id: string, reason: string) => {
    try {
      await cancelMutation.mutateAsync({ rfqId: id, input: { reason } });
      showToast('RFQ cancelado.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao cancelar RFQ.', 'error');
    }
  }, [cancelMutation, showToast]);

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  }, []);

  const totalPages = data?.totalPages ?? 1;
  const rfqs = data?.data ?? [];

  return (
    <div className="rfq-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Cotações (RFQ)
            {data && <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">({data.total})</span>}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Cotações enviadas a fornecedores e suas propostas.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>+ Nova Cotação</Button>
        </div>
      </div>

      {/* Filter */}
      <div className="rfq-page__filter-card">
        <Card padding="1rem" variant="outlined">
          <div className="rfq-page__filter-row">
            <div className="rfq-page__filter-select">
              <Select label="Filtrar por status" value={statusFilter} onChange={handleFilterChange}>
                {RFQ_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </div>
          </div>
        </Card>
      </div>

      {/* Content */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {data?.total ?? 0} {(data?.total ?? 0) === 1 ? 'cotação' : 'cotações'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Cotações enviadas</h3>
        </CardHeader>
        {isLoading && (
          <div className="rfq-page__loading">
            <LoadingSkeleton />
            <span className="rfq-page__loading-text">Carregando cotações...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="rfq-page__error">
            <p className="rfq-page__error-title">Erro ao carregar cotações</p>
            <p className="rfq-page__error-detail">{(error as Error).message}</p>
            <Button variant="primary" size="sm" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        )}

        {!isLoading && !error && rfqs.length === 0 && (
          <div className="rfq-page__empty">
            <p className="rfq-page__empty-text">Nenhuma cotação encontrada.</p>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>Criar primeira cotação</Button>
          </div>
        )}

        {!isLoading && !error && rfqs.length > 0 && (
          <div className="rfq-page__table-wrap">
            <table className="rfq-page__table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Título</th>
                  <th>Data Limite</th>
                  <th>Respostas</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rfqs.map((rfq) => (
                  <tr key={rfq.id} className="rfq-row">
                    <td className="rfq-cell--number">{rfq.number}</td>
                    <td className="rfq-cell--title">{rfq.title}</td>
                    <td className="rfq-cell--date">{formatDate(rfq.deadline)}</td>
                    <td className="rfq-cell--responses">{rfq.vendorResponses?.length ?? 0}</td>
                    <td className="rfq-cell--status">
                      <Badge variant={statusBadge(rfq.status).variant} size="sm" dot>
                        {statusBadge(rfq.status).label}
                      </Badge>
                    </td>
                    <td className="rfq-cell--actions">
                      <div className="rfq-actions">
                        <Button variant="ghost" size="sm" onClick={() => setDetailId(rfq.id)}>
                          Detalhes
                        </Button>
                        {rfq.status === 'DRAFT' && (
                          <Button variant="ghost" size="sm" loading={sendMutation.isPending} onClick={() => handleSend(rfq.id)}>
                            Enviar
                          </Button>
                        )}
                        {(rfq.status === 'DRAFT' || rfq.status === 'SENT' || rfq.status === 'QUOTED') && (
                          <Button variant="ghost" size="sm" onClick={() => {
                            const reason = prompt('Motivo do cancelamento:');
                            if (reason) handleCancelRfq(rfq.id, reason);
                          }} className="rfq-btn--cancel">
                            Cancelar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && !error && totalPages > 1 && (
          <div className="rfq-page__pagination">
            <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {detailId && (
        <RfqDetailModal
          isOpen={detailId !== null}
          onClose={() => setDetailId(null)}
          rfqId={detailId}
          showToast={showToast}
        />
      )}

      {/* Create Modal */}
      <CreateRfqModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        showToast={showToast}
      />

      {/* Toast */}
      {toastMessage && (
        <div role="alert" className={`rfq-toast rfq-toast--${toastVariant}`}>
          {toastMessage}
          <button onClick={() => setToastMessage(null)} aria-label="Fechar" className="rfq-toast__close">&times;</button>
        </div>
      )}

      <style>{`
        .rfq-page { padding: 1.5rem; }
        .rfq-page__filter-card { margin-bottom: 1rem; }
        .rfq-page__filter-row { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
        .rfq-page__filter-select { min-width: 220px; }
        .rfq-page__loading { display: flex; align-items: center; justify-content: center; padding: 3rem; gap: 0.75rem; color: #43474e; }
        .rfq-page__loading-text { font-size: 0.875rem; }
        .rfq-page__error { padding: 3rem 2rem; text-align: center; color: #ba1a1a; }
        .rfq-page__error-title { font-weight: 600; margin: 0 0 0.5rem; }
        .rfq-page__error-detail { font-size: 0.875rem; margin: 0 0 1rem; color: #74777f; }
        .rfq-page__empty { padding: 3rem 2rem; text-align: center; color: #74777f; }
        .rfq-page__empty-text { margin: 0 0 1rem; font-size: 0.875rem; }
        .rfq-page__table-wrap { overflow-x: auto; }
        .rfq-page__table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .rfq-page__table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .rfq-page__table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; }
        .rfq-row:hover td { background: #eceef0; }
        .rfq-cell--number { font-weight: 600; font-family: monospace; }
        .rfq-cell--title { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 280px; }
        .rfq-cell--date { white-space: nowrap; }
        .rfq-cell--responses { text-align: center; }
        .rfq-cell--actions { white-space: nowrap; }
        .rfq-actions { display: flex; gap: 0.25rem; align-items: center; }
        .rfq-btn--cancel { color: #ba1a1a; }
        .rfq-page__pagination { padding: 1rem; border-top: 1px solid #eceef0; }
        .rfq-toast { position: fixed; bottom: 1.5rem; right: 1.5rem; padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.875rem; font-weight: 500; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000; max-width: 400px; }
        .rfq-toast--success { background: #d3f5d6; border: 1px solid #b6ebbb; color: #2e7d32; }
        .rfq-toast--error { background: #ffdad6; border: 1px solid #ffb4ab; color: #ba1a1a; }
        .rfq-toast__close { margin-left: 0.75rem; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0; line-height: 1; }
        .rfq-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 1.5rem; }
        .rfq-detail-label { margin: 0; font-size: 0.75rem; color: #74777f; }
        .rfq-detail-value { margin: 0.125rem 0 0; font-weight: 600; }
        .rfq-detail-section { margin-top: 1.5rem; }
        .rfq-detail-section h3 { font-size: 1rem; font-weight: 600; margin: 0 0 0.75rem; }
        .rfq-detail-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .rfq-detail-table th { text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; position: sticky; top: 0; z-index: 1;}
        .rfq-detail-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; }
        .rfq-detail-table th.right, .rfq-detail-table td.right { text-align: right; }
        .rfq-response-card { padding: 1rem; margin-bottom: 0.75rem; background: #f2f4f6; border: 1px solid #eceef0; border-radius: 8px; }
        .rfq-response-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .rfq-response-vendor { font-weight: 600; font-size: 0.9375rem; }
        .rfq-response-total { font-weight: 700; font-size: 1rem; color: #002045; }
        .rfq-response-meta { font-size: 0.8125rem; color: #43474e; }
        .rfq-winner-badge { background: #d3f5d6; color: #2e7d32; padding: 0.25rem 0.5rem; border-radius: 4px; font-size: 0.75rem; font-weight: 600; }
        .rfq-form { display: flex; flex-direction: column; gap: 1rem; }
        .rfq-form-actions { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1rem; }
        .rfq-items-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
        .rfq-items-title { font-size: 0.875rem; font-weight: 600; color: #191c1e; }
        .rfq-items-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .rfq-items-table th { text-align: left; padding: 0.5rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; position: sticky; top: 0; z-index: 1;}
        .rfq-items-table td { padding: 0.25rem 0.5rem; border-bottom: 1px solid #eceef0; }
        .rfq-items-table th.right, .rfq-items-table td.right { text-align: right; }
        .rfq-items-table .remove-cell { padding: 0.25rem 0.5rem; text-align: center; }
        .rfq-remove-btn { background: none; border: none; color: #ba1a1a; cursor: pointer; font-size: 1rem; }
        .rfq-create-title { margin: 0 0 0.75rem; font-size: 1rem; font-weight: 600; }
        .response-actions { margin-top: 1rem; text-align: right; }
      `}</style>
    </div>
  );
}

/* ── Detail Modal ── */

function RfqDetailModal({ isOpen, onClose, rfqId, showToast }: {
  isOpen: boolean;
  onClose: () => void;
  rfqId: string | null;
  showToast: (msg: string, variant: 'success' | 'error') => void;
}) {
  const { data: rfq, isLoading, error, refetch } = useRfq(rfqId ?? '');
  const awardMutation = useAwardRfq();
  const [respondModalOpen, setRespondModalOpen] = useState(false);

  const handleAward = useCallback(async (responseId: string) => {
    if (!rfqId) return;
    try {
      await awardMutation.mutateAsync({ rfqId, input: { responseId } });
      showToast('Cotação adjudicada com sucesso.', 'success');
      refetch();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao adjudicar.', 'error');
    }
  }, [rfqId, awardMutation, showToast, refetch]);

  const winnerResponse = rfq?.vendorResponses?.find((r) => r.isWinner);
  const canAward = rfq?.status === 'QUOTED' || rfq?.status === 'SENT';
  const canRespond = rfq?.status === 'SENT' || rfq?.status === 'QUOTED';

  const footer = (
    <Button variant="outline" onClick={onClose}>Fechar</Button>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`RFQ: ${rfq?.number ?? ''}`} footer={footer} size="lg">
        {isLoading && (
          <div className="rfq-page__loading">
            <LoadingSkeleton />
            <span>Carregando...</span>
          </div>
        )}
        {error && !isLoading && (
          <div className="rfq-page__error">
            <p className="rfq-page__error-title">Erro ao carregar cotação</p>
            <p className="rfq-page__error-detail">{(error as Error).message}</p>
            <Button variant="primary" size="sm" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        )}
        {rfq && !isLoading && (
          <div>
            <div className="rfq-detail-grid">
              <div>
                <p className="rfq-detail-label">Título</p>
                <p className="rfq-detail-value">{rfq.title}</p>
              </div>
              <div>
                <p className="rfq-detail-label">Status</p>
                <p className="rfq-detail-value">
                  <Badge variant={statusBadge(rfq.status).variant} size="sm" dot>
                    {statusBadge(rfq.status).label}
                  </Badge>
                </p>
              </div>
              <div>
                <p className="rfq-detail-label">Data Limite</p>
                <p className="rfq-detail-value">{formatDate(rfq.deadline)}</p>
              </div>
              <div>
                <p className="rfq-detail-label">Entrega Prevista</p>
                <p className="rfq-detail-value">{formatDate(rfq.expectedDeliveryDate ?? null)}</p>
              </div>
              {rfq.description && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <p className="rfq-detail-label">Descrição</p>
                  <p className="rfq-detail-value">{rfq.description}</p>
                </div>
              )}
              {winnerResponse && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <p className="rfq-detail-label">Vencedor</p>
                  <p className="rfq-detail-value">
                    <span className="rfq-winner-badge">{winnerResponse.vendorName}</span>
                    {' '}— {formatCurrency(winnerResponse.total)}
                  </p>
                </div>
              )}
            </div>

            {/* Items */}
            <div className="rfq-detail-section">
              <h3>Itens da Cotação</h3>
              <div style={{ overflowX: 'auto' }}>
                <table className="rfq-detail-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th className="right">Qtd</th>
                      <th>Unidade</th>
                      <th>Especificações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rfq.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: 500 }}>{item.description}</td>
                        <td className="right">{item.quantity}</td>
                        <td>{item.unit}</td>
                        <td className="rfq-sku">{item.sku}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Responses */}
            <div className="rfq-detail-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0 }}>Respostas dos Fornecedores</h3>
                {canRespond && (
                  <Button variant="outline" size="sm" onClick={() => setRespondModalOpen(true)}>
                    + Registrar Resposta
                  </Button>
                )}
              </div>
              {(!rfq.vendorResponses || rfq.vendorResponses.length === 0) && (
                <p style={{ color: '#74777f', fontSize: '0.875rem', padding: '1rem 0' }}>
                  Nenhuma resposta recebida ainda.
                </p>
              )}
              {rfq.vendorResponses?.map((resp) => (
                <div key={resp.id} className="rfq-response-card">
                  <div className="rfq-response-card-header">
                    <div>
                      <span className="rfq-response-vendor">{resp.vendorName}</span>
                      {resp.isWinner && <span className="rfq-winner-badge" style={{ marginLeft: '0.5rem' }}>Vencedor</span>}
                    </div>
                    <span className="rfq-response-total">{formatCurrency(resp.total)}</span>
                  </div>
                  <div className="rfq-response-meta">
                    {resp.vendorDocument} &middot; {resp.deliveryEstimateDays} dias para entrega &middot;
                    Válido por {resp.validityDays} dias
                  </div>
                  {resp.notes && <div className="rfq-response-meta" style={{ marginTop: '0.25rem' }}>{resp.notes}</div>}
                  {canAward && !resp.isWinner && !winnerResponse && (
                    <div className="response-actions">
                      <Button variant="primary" size="sm" loading={awardMutation.isPending} onClick={() => handleAward(resp.id)}>
                        Adjudicar
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Respond Modal */}
      {rfq && (
        <RespondModal
          isOpen={respondModalOpen}
          onClose={() => setRespondModalOpen(false)}
          rfqId={rfq.id}
          rfqItems={rfq.items}
          showToast={showToast}
        />
      )}
    </>
  );
}

/* ── Respond Modal ── */

function RespondModal({ isOpen, onClose, rfqId, rfqItems, showToast }: {
  isOpen: boolean;
  onClose: () => void;
  rfqId: string;
  rfqItems: RfqItem[];
  showToast: (msg: string, variant: 'success' | 'error') => void;
}) {
  const submitMutation = useSubmitRfqResponse();

  const [vendorId, setVendorId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [vendorDocument, setVendorDocument] = useState('');
  const [vendorContactName, setVendorContactName] = useState('');
  const [vendorContactEmail, setVendorContactEmail] = useState('');
  const [deliveryEstimateDays, setDeliveryEstimateDays] = useState('7');
  const [validityDays, setValidityDays] = useState('15');
  const [notes, setNotes] = useState('');
  const [responseItems, setResponseItems] = useState(
    rfqItems.map((item, idx) => ({
      rfqItemIndex: idx,
      productId: item.productId,
      productName: item.description,
      quantity: item.quantity,
      unitPrice: '',
      deliveryDays: deliveryEstimateDays,
    }))
  );

  useEffect(() => {
    // Reset when modal opens
    setVendorId('');
    setVendorName('');
    setVendorDocument('');
    setVendorContactName('');
    setVendorContactEmail('');
    setDeliveryEstimateDays('7');
    setValidityDays('15');
    setNotes('');
    setResponseItems(
      rfqItems.map((item, idx) => ({
        rfqItemIndex: idx,
        productId: item.productId,
        productName: item.description,
        quantity: item.quantity,
        unitPrice: '',
        deliveryDays: '7',
      }))
    );
  }, [isOpen, rfqItems]);

  const updateItem = useCallback((idx: number, field: string, value: string) => {
    setResponseItems((prev) => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  }, []);

  const total = responseItems.reduce((sum, item) => {
    return sum + (parseFloat(item.unitPrice) || 0) * item.quantity;
  }, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendorId || !vendorName || !vendorDocument) return;

    const parsedItems = responseItems
      .filter((item) => item.unitPrice)
      .map((item) => ({
        rfqItemIndex: item.rfqItemIndex,
        productId: item.productId,
        /* `responseItems` ja guarda o nome resolvido do item da cotacao; aqui
           ele so e repassado. */
        productName: item.productName,
        quantity: item.quantity,
        unitPrice: parseFloat(item.unitPrice) || 0,
        deliveryDays: parseInt(item.deliveryDays, 10) || 7,
      }));

    if (parsedItems.length === 0) return;

    const input: SubmitRfqResponseInput = {
      vendorId,
      vendorName,
      vendorDocument,
      vendorContactName: vendorContactName || undefined,
      vendorContactEmail: vendorContactEmail || undefined,
      items: parsedItems,
      total,
      deliveryEstimateDays: parseInt(deliveryEstimateDays, 10) || 7,
      validityDays: parseInt(validityDays, 10) || 15,
      notes: notes || undefined,
    };

    try {
      await submitMutation.mutateAsync({ rfqId, input });
      showToast('Resposta registrada com sucesso.', 'success');
      onClose();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao registrar resposta.', 'error');
    }
  };

  const valid = vendorId && vendorName && vendorDocument && responseItems.some((i) => i.unitPrice);

  const footer = (
    <>
      <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
      <Button variant="primary" type="submit" form="respond-form" loading={submitMutation.isPending} disabled={!valid}>
        Registrar Resposta
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Registrar Resposta de Fornecedor" footer={footer}>
      <form id="respond-form" onSubmit={handleSubmit}>
        <div className="rfq-form">
          <Input label="ID do Fornecedor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} required />
          <Input label="Nome do Fornecedor" value={vendorName} onChange={(e) => setVendorName(e.target.value)} required />
          <Input label="CNPJ/CPF" value={vendorDocument} onChange={(e) => setVendorDocument(e.target.value)} required />
          <Input label="Contato (nome)" value={vendorContactName} onChange={(e) => setVendorContactName(e.target.value)} />
          <Input label="Contato (email)" type="email" value={vendorContactEmail} onChange={(e) => setVendorContactEmail(e.target.value)} />

          <div className="rfq-items-header">
            <span className="rfq-items-title">Preços por Item</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="rfq-items-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th className="right">Qtd</th>
                  <th className="right">Vr. Unit.</th>
                  <th className="right">Prazo (dias)</th>
                </tr>
              </thead>
              <tbody>
                {responseItems.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: 500 }}>{item.productName}</td>
                    <td className="right">{item.quantity}</td>
                    <td className="right">
                      <Input label="" type="number" min="0" step="0.01" value={item.unitPrice}
                        onChange={(e) => updateItem(idx, 'unitPrice', e.target.value)} placeholder="0,00" />
                    </td>
                    <td className="right">
                      <Input label="" type="number" min="1" value={item.deliveryDays}
                        onChange={(e) => updateItem(idx, 'deliveryDays', e.target.value)} placeholder="7" />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2}></td>
                  <td className="right" style={{ fontWeight: 700, padding: '0.5rem' }}>Total</td>
                  <td className="right" style={{ fontWeight: 700, padding: '0.5rem' }}>{formatCurrency(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <Input label="Prazo de Entrega (dias)" type="number" min="1" value={deliveryEstimateDays}
            onChange={(e) => setDeliveryEstimateDays(e.target.value)} />
          <Input label="Validade da Proposta (dias)" type="number" min="1" value={validityDays}
            onChange={(e) => setValidityDays(e.target.value)} />
          <Input label="Observações" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Condições de pagamento, frete, etc." />
        </div>
      </form>
    </Modal>
  );
}

/* ── Create RFQ Modal ── */

function CreateRfqModal({ isOpen, onClose, showToast }: {
  isOpen: boolean;
  onClose: () => void;
  showToast: (msg: string, variant: 'success' | 'error') => void;
}) {
  const createMutation = useCreateRfq();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [awardCriteria, setAwardCriteria] = useState('LOWER_PRICE');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentCondition, setPaymentCondition] = useState('');
  const [freightResponsibility, setFreightResponsibility] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [items, setItems] = useState([
    { productId: '', productName: '', quantity: '1', unit: 'UN', specifications: '' },
  ]);

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setDeadline('');
      setExpectedDeliveryDate('');
      setAwardCriteria('LOWER_PRICE');
      setPaymentTerms('');
      setPaymentCondition('');
      setFreightResponsibility('');
      setInternalNotes('');
      setItems([{ productId: '', productName: '', quantity: '1', unit: 'UN', specifications: '' }]);
    }
  }, [isOpen]);

  const addRow = useCallback(() => {
    setItems((prev) => [...prev, { productId: '', productName: '', quantity: '1', unit: 'UN', specifications: '' }]);
  }, []);

  const removeRow = useCallback((idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateItem = useCallback((idx: number, field: string, value: string) => {
    setItems((prev) => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !deadline || items.length === 0) return;

    const parsedItems = items
      .filter((item) => item.productId)
      .map((item) => ({
        productId: item.productId,
        productName: item.productName || item.productId,
        quantity: parseInt(item.quantity, 10) || 1,
        unit: item.unit || 'UN',
        specifications: item.specifications || undefined,
      }));

    if (parsedItems.length === 0) return;

    const input: CreateRfqInput = {
      title,
      description: description || undefined,
      items: parsedItems,
      deadline,
      expectedDeliveryDate: expectedDeliveryDate || undefined,
      awardCriteria: awardCriteria || undefined,
      paymentTerms: paymentTerms || undefined,
      paymentCondition: paymentCondition || undefined,
      freightResponsibility: freightResponsibility || undefined,
      internalNotes: internalNotes || undefined,
    };

    try {
      await createMutation.mutateAsync(input);
      showToast('Cotação criada com sucesso.', 'success');
      onClose();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao criar cotação.', 'error');
    }
  };

  const valid = title && deadline && items.some((i) => i.productId);

  const footer = (
    <>
      <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
      <Button variant="primary" type="submit" form="create-rfq-form" loading={createMutation.isPending} disabled={!valid}>
        Criar Cotação
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nova Cotação (RFQ)" footer={footer} size="lg">
      <form id="create-rfq-form" onSubmit={handleSubmit}>
        <div className="rfq-form">
          <Input label="Título" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Ex.: Cotação de matérias-primas Q3" />
          <Input label="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descrição opcional" />

          <div className="rfq-items-header">
            <span className="rfq-items-title">Itens</span>
            <Button variant="outline" size="sm" type="button" onClick={addRow}>+ Adicionar Item</Button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="rfq-items-table">
              <thead>
                <tr>
                  <th>Produto ID</th>
                  <th>Produto</th>
                  <th className="right">Qtd</th>
                  <th>Un.</th>
                  <th>Especificações</th>
                  <th style={{ width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx}>
                    <td><Input label="" value={item.productId} onChange={(e) => updateItem(idx, 'productId', e.target.value)} placeholder="ID" /></td>
                    <td><Input label="" value={item.productName} onChange={(e) => updateItem(idx, 'productName', e.target.value)} placeholder="Nome" /></td>
                    <td className="right"><Input label="" type="number" min="1" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} placeholder="1" /></td>
                    <td><Input label="" value={item.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)} placeholder="UN" /></td>
                    <td><Input label="" value={item.specifications} onChange={(e) => updateItem(idx, 'specifications', e.target.value)} placeholder="Opcional" /></td>
                    <td className="remove-cell">
                      {items.length > 1 && (
                        <button type="button" onClick={() => removeRow(idx)} className="rfq-remove-btn" aria-label="Remover item">&times;</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Data Limite" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} required />
            <Input label="Entrega Prevista" type="date" value={expectedDeliveryDate} onChange={(e) => setExpectedDeliveryDate(e.target.value)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Critério de Adjudicação" value={awardCriteria} onChange={(e) => setAwardCriteria(e.target.value)} placeholder="LOWER_PRICE" />
            <Input label="Responsabilidade Frete" value={freightResponsibility} onChange={(e) => setFreightResponsibility(e.target.value)} placeholder="CIF / FOB" />
          </div>

          <Input label="Condições de Pagamento" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="Ex.: 30/60/90 dias" />
          <Input label="Observações Internas" value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} placeholder="Notas internas" />
        </div>
      </form>
    </Modal>
  );
}
